import { useSelector } from 'react-redux'
import { useCallback, useEffect } from 'react'
import { useFormikContext } from 'formik'
import {
  CreateAgreementFormPacketModel,
  CreateAgreementTemplateDetails,
} from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import { useLeadOrOppDetails } from '@user/components/Agreements/AgreementFormPacketDialog/hooks/useLeadOrOppDetails'
import { selectAllServiceLocations } from '@user/store/entityLocations'
import { ENTITY_TYPES, getServiceLocationTitle } from '@sales-center/common'

export function useAgreementPacketCardActions(entityType: ENTITY_TYPES) {
  const { setValues, values, validateForm } =
    useFormikContext<CreateAgreementFormPacketModel>()
  const services = useSelector(selectAllLeadOppProducts)
  const details = useLeadOrOppDetails(entityType)
  const serviceLocations = useSelector(selectAllServiceLocations)

  useEffect(() => {
    setValues((v) => {
      return {
        ...v,
        templates: v.templates
          .map((template) => {
            if ('templateId' in template) {
              return {
                ...template,
                services: template.services
                  ?.map((service) => services.find((s) => s.id === service.id))
                  .filter((service) => service !== undefined),
              }
            }
            return {
              ...template,
            }
          })
          .filter((template) => {
            if ('templateId' in template) {
              return template.services && template.services?.length > 0
            }
            return true
          }),
      }
    })
  }, [services, setValues, validateForm])

  useEffect(() => {
    validateForm()
  }, [details?.externalLocationCode, validateForm])

  const handleTemplateDelete = useCallback(
    (index: number) => {
      setValues((v) => ({
        ...v,
        templates: v.templates.filter((_, i) => i !== index),
      }))
    },
    [setValues],
  )

  const handleDetailInclude = useCallback(
    (templateId: number, checked: boolean) => {
      const tempData = [...values.templates]

      const index = tempData.findIndex(
        (item: CreateAgreementTemplateDetails) =>
          item.templateId === templateId,
      )

      tempData[index] = {
        ...tempData[index],
        includeDetail: checked,
      }

      setValues((v) => ({
        ...v,
        templates: tempData,
      }))
    },
    [setValues, values.templates],
  )

  const getServiceLocationName = useCallback(
    (locationId: string) => {
      const location = serviceLocations.find(
        (sv) => sv.id == Number(locationId),
      )
      return location ? getServiceLocationTitle(location) || '' : ''
    },
    [serviceLocations],
  )

  return {
    handleTemplateDelete,
    handleDetailInclude,
    getServiceLocationName,
  }
}
